// Workbook view — the .xlsx surface of DocView (docType 'workbook').
//
// The version's full model JSON (sheets, cells, formulas, formats) lives in
// S3 (version.workbookS3Key) and is fetched through the presigned-download
// path, same as spilled clause arrays. This component renders:
//   - a sheet-tabbed grid with a formula toggle and house-style hints
//   - the model profile (sheet roles, key drivers, reviewer findings, smells)
//   - per-version cell-level change manifests (the tracked-edit trail)
//   - the workbook agent bar (natural-language versioned edits)
//   - Excel download of any version (authored server-side, house style)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getAssetDownloadUrl } from '../data/uploadService';
import { aiWorkbookAgent, aiWorkbookAudit, downloadWordExport, isAiConfigured } from '../data/legalApi';
import { useToast } from './Toast';
import './WorkbookView.css';

const MAX_ROWS = 400;
const MAX_COLS = 40;

function colLetter(n) {
  let s = '';
  let x = n;
  while (x > 0) {
    const r = (x - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    x = Math.floor((x - 1) / 26);
  }
  return s;
}
const parseA1 = (ref) => {
  const m = /^([A-Z]+)(\d+)$/.exec(ref);
  if (!m) return null;
  let col = 0;
  for (const ch of m[1]) col = col * 26 + (ch.charCodeAt(0) - 64);
  return { row: Number(m[2]), col };
};

// Approximate display formatting from the cell's number format string —
// Excel is authoritative; this is a readable preview.
function fmtValue(v, fmt) {
  if (v == null || v === '') return '';
  if (typeof v !== 'number') {
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v) && fmt && /[my]/i.test(fmt)) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
      }
    }
    return String(v);
  }
  if (fmt && fmt.includes('%')) {
    const dec = /0\.0+%/.test(fmt) ? (fmt.match(/0\.(0+)%/)?.[1].length || 1) : 0;
    return `${(v * 100).toFixed(dec)}%`;
  }
  if (fmt && /x"?$|"x"/.test(fmt)) return `${v.toFixed(1)}x`;
  if (fmt && (fmt.includes('#,##0') || fmt.includes('$'))) {
    const dec = /0\.00/.test(fmt) ? 2 : 0;
    const s = Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    const cur = fmt.includes('$') ? '$' : '';
    return v < 0 ? `(${cur}${s})` : `${cur}${s}`;
  }
  if (Number.isInteger(v)) return String(v);
  return String(Math.round(v * 10000) / 10000);
}

const cellText = (cell, showFormulas) => {
  if (!cell) return '';
  if (showFormulas && cell.f) return `=${cell.f}`;
  if (cell.v == null && cell.f) return '·'; // formula whose value Excel computes on open
  return fmtValue(cell.v, cell.fmt);
};

function SheetGrid({ sheet, showFormulas, changedRefs, focusRef }) {
  // ?cell=<A1> spotlight — scroll the named cell into view and flash it
  // (the voice agent points at figures this way mid-walkthrough).
  const focusTd = useRef(null);
  useEffect(() => {
    if (focusTd.current) focusTd.current.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  }, [focusRef, sheet]);

  const { grid, rows, cols, clipped } = useMemo(() => {
    const byRef = sheet.cells || {};
    let maxR = 0;
    let maxC = 0;
    for (const ref of Object.keys(byRef)) {
      const p = parseA1(ref);
      if (!p) continue;
      maxR = Math.max(maxR, p.row);
      maxC = Math.max(maxC, p.col);
    }
    const r = Math.min(maxR, MAX_ROWS);
    const c = Math.min(Math.max(maxC, 1), MAX_COLS);
    const g = {};
    for (const [ref, cell] of Object.entries(byRef)) {
      const p = parseA1(ref);
      if (p && p.row <= r && p.col <= c) g[ref] = cell;
    }
    return { grid: g, rows: r, cols: c, clipped: maxR > MAX_ROWS || maxC > MAX_COLS };
  }, [sheet]);

  if (!rows) return <div className="wb-empty">This sheet has no populated cells.</div>;

  return (
    <div className="wb-grid-wrap">
      <table className="wb-grid">
        <thead>
          <tr>
            <th className="wb-rowhead" aria-label="row" />
            {Array.from({ length: cols }, (_, i) => (
              <th key={i} className="wb-colhead">{colLetter(i + 1)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, ri) => {
            const rowNum = ri + 1;
            return (
              <tr key={rowNum}>
                <td className="wb-rowhead">{rowNum}</td>
                {Array.from({ length: cols }, (_, ci) => {
                  const ref = `${colLetter(ci + 1)}${rowNum}`;
                  const cell = grid[ref];
                  const changed = changedRefs && changedRefs.has(ref);
                  const focused = focusRef === ref;
                  const cls = [
                    'wb-cell',
                    cell?.s ? `wb-s-${cell.s}` : '',
                    cell?.f ? 'wb-has-f' : '',
                    typeof cell?.v === 'number' || cell?.f ? 'wb-num' : '',
                    changed ? 'wb-changed' : '',
                    focused ? 'wb-focus' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <td key={ref} ref={focused ? focusTd : undefined} className={cls} title={cell?.f ? `=${cell.f}` : undefined}>
                      {cellText(cell, showFormulas)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {clipped && <div className="wb-clip u-xs">Preview clipped at {MAX_ROWS} rows × {MAX_COLS} columns — the Excel download has everything.</div>}
    </div>
  );
}

function ChangesPanel({ version }) {
  const ops = version?.manifest?.ops || [];
  if (!ops.length) {
    return <div className="wb-empty">No cell-level manifest on this version{version?.number === 1 ? ' — v1 is the imported original.' : '.'}</div>;
  }
  const showCell = (c) => {
    if (!c) return <span className="wb-diff-empty">—</span>;
    return (
      <span>
        {c.f ? <code>={c.f}</code> : String(c.v ?? '')}
        {c.fmt ? <span className="u-xs wb-diff-fmt"> · {c.fmt}</span> : null}
      </span>
    );
  };
  return (
    <div className="wb-changes">
      {version.manifest.summary && <div className="wb-changes-summary">{version.manifest.summary}</div>}
      <table className="wb-changes-table">
        <thead>
          <tr><th>Sheet</th><th>Cell</th><th>Before</th><th>After</th><th>Why</th></tr>
        </thead>
        <tbody>
          {ops.slice(0, 200).map((op, i) => (
            <tr key={i}>
              <td>{op.sheet || ''}</td>
              <td><code>{op.ref || op.op}</code></td>
              <td>{showCell(op.before)}</td>
              <td>{showCell(op.after)}</td>
              <td className="u-xs">{op.rationale || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {ops.length > 200 && <div className="u-xs wb-clip">{ops.length - 200} more changes in the manifest…</div>}
      {version.manifest.opCount > ops.length && (
        <div className="u-xs wb-clip">{version.manifest.opCount} total changes ({version.manifest.opCount - ops.length} not stored inline).</div>
      )}
    </div>
  );
}

function ProfileCard({ doc }) {
  const wp = doc.workbookProfile;
  if (!wp) return null;
  const ai = wp.ai;
  return (
    <details className="wb-profile">
      <summary>
        <strong>{ai?.modelType || 'Workbook'}</strong>
        {ai?.periodicity && ai.periodicity !== 'none' ? ` · ${ai.periodicity}` : ''}
        {' · '}{(wp.sheets || []).length} sheets
        {wp.smells?.length ? <span className="wb-smell-chip"> · {wp.smells.length} model smell{wp.smells.length === 1 ? '' : 's'}</span> : ' · clean'}
      </summary>
      {ai?.summary && <p>{ai.summary}</p>}
      <div className="wb-profile-grid">
        <div>
          <div className="u-xs wb-profile-h">Sheets</div>
          {(wp.sheets || []).map((s) => (
            <div key={s.name} className="u-xs">
              <strong>{s.name}</strong> — {s.role}{s.dependsOn?.length ? ` · pulls from ${s.dependsOn.join(', ')}` : ''}
            </div>
          ))}
        </div>
        {ai?.keyDrivers?.length ? (
          <div>
            <div className="u-xs wb-profile-h">Key drivers</div>
            {ai.keyDrivers.map((d, i) => (
              <div key={i} className="u-xs"><strong>{d.label}</strong>{d.value ? ` = ${d.value}` : ''}{d.sheet ? ` (${d.sheet}${d.ref ? `!${d.ref}` : ''})` : ''}</div>
            ))}
          </div>
        ) : null}
        {wp.smells?.length ? (
          <div>
            <div className="u-xs wb-profile-h">Model smells</div>
            {wp.smells.slice(0, 8).map((s, i) => (
              <div key={i} className="u-xs">⚠️ [{s.sheet}] {s.note}</div>
            ))}
          </div>
        ) : null}
        {ai?.findings?.length ? (
          <div>
            <div className="u-xs wb-profile-h">Reviewer findings</div>
            {ai.findings.map((f, i) => <div key={i} className="u-xs">• {f}</div>)}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default function WorkbookView({ scope, deal, doc, versions, can }) {
  const toast = useToast();
  const sorted = useMemo(
    () => [...(versions || [])].sort((a, b) => a.number - b.number),
    [versions]
  );
  const [viewVersionId, setViewVersionId] = useState(null);
  const version = sorted.find((v) => v.id === (viewVersionId || doc.currentVersionId)) || sorted[sorted.length - 1];

  const [model, setModel] = useState(null);
  const [modelErr, setModelErr] = useState('');
  const [activeSheet, setActiveSheet] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFormulas, setShowFormulas] = useState(false);
  const [tab, setTab] = useState('grid'); // grid | changes
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [auditing, setAuditing] = useState(false);

  useEffect(() => {
    let alive = true;
    setModel(null);
    setModelErr('');
    if (!version?.workbookS3Key) {
      setModelErr(version ? 'This version has no workbook model.' : '');
      return undefined;
    }
    getAssetDownloadUrl({ key: version.workbookS3Key })
      .then((url) => fetch(url))
      .then((r) => { if (!r.ok) throw new Error(`model fetch failed (${r.status})`); return r.json(); })
      .then((m) => {
        if (!alive) return;
        setModel(m);
        setActiveSheet((prev) => (m.sheets.some((s) => s.name === prev) ? prev : (m.sheets.find((s) => !s.hidden)?.name || '')));
      })
      .catch((err) => { if (alive) setModelErr(err.message); });
    return () => { alive = false; };
  }, [version?.id, version?.workbookS3Key]);

  // ?cell=E26 spotlights one cell on the active sheet (voice-agent pointer).
  const cellParam = (searchParams.get('cell') || '').toUpperCase();
  const focusCell = /^[A-Z]{1,3}\d{1,5}$/.test(cellParam) ? cellParam : '';

  // Deep-linkable tabs (?sheet=Synergy%20Validation) — shareable links, and
  // the voice agent navigates here mid-walkthrough to flip tabs.
  useEffect(() => {
    const wanted = searchParams.get('sheet');
    if (!wanted || !model) return;
    const match = model.sheets.find((s) => s.name.toLowerCase() === String(wanted).toLowerCase());
    if (match && match.name !== activeSheet) setActiveSheet(match.name);
  }, [searchParams, model, activeSheet]);

  // Cells this version changed (highlighted in the grid).
  const changedRefs = useMemo(() => {
    const set = new Map(); // sheet -> Set(refs)
    for (const op of version?.manifest?.ops || []) {
      if (!op.sheet || !op.ref || !/^[A-Z]+\d+$/.test(op.ref)) continue;
      if (!set.has(op.sheet)) set.set(op.sheet, new Set());
      set.get(op.sheet).add(op.ref);
    }
    return set;
  }, [version]);

  const sheet = model?.sheets.find((s) => s.name === activeSheet) || model?.sheets[0];
  const leaseHeld = !!(doc.editLease && (doc.editLease.expiresAt || 0) > Date.now());
  const progress = doc.progress && doc.progress.status === 'running' ? doc.progress : null;

  const runAgent = async (e) => {
    e.preventDefault();
    if (!instruction.trim() || busy) return;
    setBusy(true);
    try {
      const r = await aiWorkbookAgent({
        orgId: scope.org, dealId: scope.deal, docId: doc.id, instruction: instruction.trim(),
      });
      setInstruction('');
      setViewVersionId(null); // snap to the new current version
      toast(`Published v${r.number}: ${r.summary || 'edit applied'} (${r.opCount} cell change${r.opCount === 1 ? '' : 's'})`, 'ok', 7000);
    } catch (err) {
      toast(err.message, 'danger', 8000);
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setExporting(true);
    try {
      await downloadWordExport({ orgId: scope.org, dealId: scope.deal, docId: doc.id, versionId: version?.id });
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="cs-page wb-page">
      <div className="wb-head">
        <div>
          <div className="u-xs"><Link to={`/deals/${scope.deal}`}>{deal?.name || 'Project'}</Link> / workbook</div>
          <h1 className="wb-title">{doc.title}</h1>
        </div>
        <div className="wb-head-actions">
          {sorted.length > 1 && (
            <select
              className="cs-input wb-vselect"
              value={version?.id || ''}
              onChange={(e) => setViewVersionId(e.target.value === doc.currentVersionId ? null : e.target.value)}
            >
              {[...sorted].reverse().map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.number}{v.id === doc.currentVersionId ? ' (current)' : ''} — {v.label || v.note || ''}
                </option>
              ))}
            </select>
          )}
          {isAiConfigured() && can && can('agent') && (
            <button
              type="button"
              className="cs-btn"
              disabled={auditing || !version?.workbookS3Key}
              data-tip="Full model audit — structure, assumptions, tie-outs, fix list — filed as a memo document in the project"
              onClick={async () => {
                setAuditing(true);
                try {
                  const r = await aiWorkbookAudit({ orgId: scope.org, dealId: scope.deal, docId: doc.id });
                  toast(`Audit filed: "${r.title}"${r.verdict ? ` — ${r.verdict}` : ''}`, 'ok');
                } catch (err) {
                  toast(err.message, 'danger');
                } finally {
                  setAuditing(false);
                }
              }}
            >
              {auditing ? 'Auditing…' : 'Audit model'}
            </button>
          )}
          <button type="button" className="cs-btn" onClick={download} disabled={exporting || !version?.workbookS3Key}>
            {exporting ? 'Preparing…' : '⬇︎ Excel'}
          </button>
        </div>
      </div>

      <ProfileCard doc={doc} />

      {progress && (
        <div className="cs-banner b-info wb-progress">
          Workbook agent is working — {progress.phase || progress.task}{progress.detail ? ` · ${progress.detail}` : ''}
        </div>
      )}

      <div className="wb-toolbar">
        <div className="wb-tabs">
          {(model?.sheets || []).filter((s) => !s.hidden).map((s) => (
            <button
              key={s.name}
              type="button"
              className={`wb-tab ${s.name === sheet?.name ? 'active' : ''}`}
              onClick={() => {
                setActiveSheet(s.name);
                const next = new URLSearchParams(searchParams);
                next.set('sheet', s.name);
                next.delete('cell'); // manual tab change drops the spotlight
                setSearchParams(next, { replace: true });
              }}
            >
              {s.name}
            </button>
          ))}
        </div>
        <div className="wb-toolbar-right">
          <label className="u-xs wb-check">
            <input type="checkbox" checked={showFormulas} onChange={(e) => setShowFormulas(e.target.checked)} />
            formulas
          </label>
          <button type="button" className={`wb-tab ${tab === 'grid' ? 'active' : ''}`} onClick={() => setTab('grid')}>Grid</button>
          <button type="button" className={`wb-tab ${tab === 'changes' ? 'active' : ''}`} onClick={() => setTab('changes')}>
            Changes{version?.manifest?.ops?.length ? ` (${version.manifest.opCount || version.manifest.ops.length})` : ''}
          </button>
        </div>
      </div>

      {tab === 'changes' ? (
        <ChangesPanel version={version} />
      ) : modelErr ? (
        <div className="cs-banner b-danger">{modelErr}</div>
      ) : !model ? (
        <div className="cs-skeleton" style={{ height: 320 }} />
      ) : sheet ? (
        <SheetGrid sheet={sheet} showFormulas={showFormulas} changedRefs={changedRefs.get(sheet.name)} focusRef={focusCell} />
      ) : (
        <div className="wb-empty">Empty workbook.</div>
      )}

      {isAiConfigured() && can && can('agent') && (
        <form className="wb-agent" onSubmit={runAgent}>
          <input
            className="cs-input wb-agent-input"
            placeholder='Instruct the workbook agent — e.g. "add a quarterly cash flow sheet", "move hardcodes to Assumptions", "extend the model to 36 months"'
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            disabled={busy || leaseHeld}
          />
          <button type="submit" className="cs-btn primary" disabled={busy || leaseHeld || !instruction.trim()}>
            {busy ? 'Working…' : leaseHeld ? 'Locked (task running)' : 'Run'}
          </button>
        </form>
      )}
      <div className="u-xs wb-note">
        Edits publish as new versions with a cell-level change manifest — pick any version above and download it as Excel.
        Formulas recalculate when the file opens in Excel.
      </div>
    </div>
  );
}
